import { Link, navigate } from "gatsby";
import React, { useState } from "react";

import {
  FaArrowRight,
  FaLock,
  FaUnlock,
  FaUser,
  FaUserPlus,
} from "../../AppReactIcons";
import {
  DiscoveryPage_InternalLink,
  DiscussionForum_InternalLink,
  Lifestyle_InternalLink,
  Login_InternalLink,
  Logout_InternalLink,
  MyDashboard_InternalLink,
  Profile_InternalLink,
  Search_InternalLink,
  Signup_InternalLink,
  GoalSettingHomepage_InternalLink,
  Hirsutism_InternalLink,
  DecisionAid_InternalLink,
} from "../../utils/urls";
import OnlyWithProfile from "../QueryUserProfile/OnlyWithProfile";
import {
  DarkAppButton,
  LoginButtonWrapper,
  LogoutButtonWrapper,
  RedAppButton,
  SignUpButtonWrapper,
} from "../buttons";
import NavbarLinksWrapper from "../pageComponents/Utils/NavbarLinksWrapper";
import useStore from "../pageComponents/Utils/Store";
import OnlyForUser from "../registration/OnlyForUser";
import { iNavbar, iNavbarView } from "../types";
import NavSidebar from "./NavSidebar";
import NavbarAppLogo from "./NavbarAppLogo";
import NavbarMenuButton from "./NavbarMenuButton";
import NavbarViewWrapper from "./NavbarViewWrapper";

const featureEnabled = process.env.GATSBY_FEATURE_FLAG === "development";

const NavbarButton: React.FC<{
  title: string;
  link: string;
  selected: boolean;
}> = ({ title, link, selected }) => {
  return (
    <Link to={link}>
      <button
        className={`transition duration-100
                  bg-white text-site-blue-dark 
                  text-sm ${
                    !selected && "hover:bg-gray-200"
                  }  px-3 leading-none 
                  rounded-full mr-3 hidden lg:block`}
      >
        <span style={{ height: "4px", width: "20px" }} className="block" />
        <span className="block py-1">{title}</span>
        <span
          style={{ height: "4px", width: "24px" }}
          className={`block ${selected && "bg-site-green"} rounded-full`}
        />
      </button>
    </Link>
  );
};

const NavbarView: React.FC<iNavbarView> = props => {
  const { links, pageTitle, SignupButton, LoginButton, LogoutButton } = props;
  const { turnQuizFalse, turnQplFalse, turnForumFalse, turnAnalyseFalse } =
    useStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const onToggleSidebar = (open: boolean) => setSidebarOpen(open);
  const menuButtonToggle = () => onToggleSidebar(!sidebarOpen);
  return (
    <React.Fragment>
      <NavbarViewWrapper>
        <div className="flex items-center">
          <NavbarAppLogo />
          <h1 className="ml-2 text-site-blue-dark text-lg border-l pl-2">
            {pageTitle}
          </h1>
        </div>
        <div className="flex items-center">
          <OnlyForUser
            redirectTo={null}
            functionalChildren={(isUser: boolean) => {
              if (isUser)
                return (
                  <>
                    <NavbarButton
                      title="Home"
                      link={"/"}
                      selected={pageTitle === "Home"}
                    />

                    <NavbarButton
                      title="Search"
                      link={Search_InternalLink}
                      selected={pageTitle === "Search"}
                    />

                    <NavbarButton
                      title="Explore"
                      link={DiscoveryPage_InternalLink}
                      selected={pageTitle === "Topics"}
                    />

                    <NavbarButton
                      title="Lifestyle"
                      link={Lifestyle_InternalLink}
                      selected={pageTitle === "Lifestyle"}
                    />
                    {featureEnabled && (
                      <NavbarButton
                        title="Decision aids"
                        link={DecisionAid_InternalLink}
                        selected={pageTitle === "Decision Aids"}
                      />
                    )}

                    <NavbarButton
                      title="Forum"
                      link={DiscussionForum_InternalLink}
                      selected={pageTitle === "Forum"}
                    />

                    <NavbarButton
                      title="My profile"
                      link={Profile_InternalLink}
                      selected={pageTitle === "My profile"}
                    />

                    <OnlyWithProfile
                      noRedirectToProfilePage={true}
                      hideLoader={true}
                      childrenWithProfileData={data => {
                        return (
                          <>
                            {pageTitle !== "Dashboard" && (
                              <div className="hidden md:block">
                                <DarkAppButton
                                  title="My dashboard"
                                  Icon={<FaUser />}
                                  onClick={() =>
                                    navigate(
                                      data && data.length
                                        ? MyDashboard_InternalLink
                                        : Profile_InternalLink
                                    )
                                  }
                                />
                              </div>
                            )}
                          </>
                        );
                      }}
                    />

                    {pageTitle === "Dashboard" && (
                      <div className="hidden lg:block">
                        <RedAppButton
                          title="Log out"
                          Icon={<FaLock />}
                          onClick={() => {
                            turnForumFalse();
                            turnQplFalse();
                            turnQuizFalse();
                            turnAnalyseFalse();
                            const logOut = confirm(
                              "Are you sure you want to log out?"
                            );
                            if (logOut) {
                              navigate(Logout_InternalLink);
                            }
                          }}
                        />
                      </div>
                    )}

                    <div className="ml-3 block lg:hidden">
                      <NavbarMenuButton onClick={() => onToggleSidebar(true)} />
                    </div>
                  </>
                );
              return (
                <>
                  <NavbarButton
                    title="Home"
                    link={"/"}
                    selected={pageTitle === "Home"}
                  />

                  <NavbarButton
                    title="Search"
                    link={Search_InternalLink}
                    selected={pageTitle === "Search"}
                  />

                  <NavbarButton
                    title="Explore"
                    link={DiscoveryPage_InternalLink}
                    selected={pageTitle === "Topics"}
                  />

                  <NavbarButton
                    title="Lifestyle"
                    link={Lifestyle_InternalLink}
                    selected={pageTitle === "Lifestyle"}
                  />
                  <NavbarButton
                    title="Decision aid"
                    link={DecisionAid_InternalLink}
                    selected={pageTitle === "Decision aid"}
                  />

                  <NavbarButton
                    title="Forum"
                    link={DiscussionForum_InternalLink}
                    selected={pageTitle === "Forum"}
                  />

                  <NavbarButton
                    title="Sign up"
                    link={Signup_InternalLink}
                    selected={false}
                  />

                  <div className="hidden md:block">
                    <DarkAppButton
                      title="My dashboard"
                      Icon={<FaArrowRight />}
                      iconOnRight={true}
                      onClick={() => navigate(Login_InternalLink)}
                    />
                  </div>

                  <div className="ml-3 block lg:hidden">
                    <NavbarMenuButton onClick={menuButtonToggle} />
                  </div>
                </>
              );
            }}
          />
        </div>
      </NavbarViewWrapper>
      <NavSidebar
        sidebarOpen={sidebarOpen}
        onToggleSidebar={onToggleSidebar}
        links={links}
        pageTitle={pageTitle}
      />
    </React.Fragment>
  );
};

const Navbar: React.FC<iNavbar> = ({ pageTitle }) => {
  const SignupButton = SignUpButtonWrapper(
    <div className="flex">
      <div className="mx-1" />
      <DarkAppButton Icon={<FaUserPlus />} />
    </div>
  );
  const LoginButton = LoginButtonWrapper(
    <div className="flex">
      <div className="mx-1" />
      <DarkAppButton Icon={<FaUnlock />} />
    </div>
  );

  const LogoutButton = LogoutButtonWrapper(
    <div className="flex">
      <div className="mx-1" />
      <RedAppButton Icon={<FaLock />} />
    </div>
  );
  return (
    <NavbarLinksWrapper>
      {NavbarLinks => (
        <NavbarView
          pageTitle={pageTitle}
          links={NavbarLinks}
          SignupButton={SignupButton}
          LoginButton={LoginButton}
          LogoutButton={LogoutButton}
        />
      )}
    </NavbarLinksWrapper>
  );
};

export default Navbar;
